class PremiumJourneyTimeline extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-reveal-ready');
    this.cardsGrid = this.querySelector('[data-pjt-cards]');
    this.cards = Array.from(this.querySelectorAll('[data-pjt-card]'));
    this.nodes = Array.from(this.querySelectorAll('[data-pjt-node]'));
    this.connector = this.querySelector('[data-pjt-connector]');
    this.connectorPaths = Array.from(this.querySelectorAll('[data-pjt-connector-line], [data-pjt-connector-glow], [data-pjt-connector-dash]'));
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.desktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.controller = new AbortController();
    this.signal = this.controller.signal;

    this.addEventListener('pointerdown', this.onPointerDown, { signal: this.signal });
    this.addEventListener('pointermove', this.onPointerMove, { passive: true, signal: this.signal });
    this.addEventListener('pointerleave', this.resetCardTilts, { signal: this.signal });
    this.addEventListener('shopify:block:select', this.onBlockSelect, { signal: this.signal });
    this.mobileQuery.addEventListener('change', this.scheduleConnector, { signal: this.signal });

    if ('ResizeObserver' in window && this.cardsGrid) {
      this.resizeObserver = new ResizeObserver(this.scheduleConnector);
      this.resizeObserver.observe(this.cardsGrid);
    } else {
      window.addEventListener('resize', this.scheduleConnector, { passive: true, signal: this.signal });
    }

    this.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', this.scheduleConnector, { once: true, signal: this.signal });
    });

    document.fonts?.ready.then(this.scheduleConnector).catch(() => {});
    this.setupReveal();
    this.scheduleConnector();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.observer?.disconnect();
    cancelAnimationFrame(this.connectorFrame);
    cancelAnimationFrame(this.tiltFrame);
    clearTimeout(this.clickTimer);
  }

  scheduleConnector = () => {
    cancelAnimationFrame(this.connectorFrame);
    this.connectorFrame = requestAnimationFrame(() => this.drawConnector());
  };

  drawConnector() {
    if (!this.connector || !this.cardsGrid || this.nodes.length < 2) return;

    const gridRect = this.cardsGrid.getBoundingClientRect();
    const width = Math.max(1, Math.round(gridRect.width));
    const topOffset = this.mobileQuery.matches ? 34 : 45;
    const height = Math.max(1, Math.round(gridRect.height + topOffset));
    const points = this.nodes.map((node) => {
      const rect = node.getBoundingClientRect();
      return {
        x: rect.left - gridRect.left + rect.width / 2,
        y: rect.top - gridRect.top + rect.height / 2 + topOffset,
      };
    });

    const path = this.mobileQuery.matches ? this.buildSquarePath(points) : this.buildDesktopPath(points);
    this.connector.setAttribute('viewBox', `0 0 ${width} ${height}`);
    this.connector.setAttribute('width', String(width));
    this.connector.setAttribute('height', String(height));
    this.connectorPaths.forEach((item) => item.setAttribute('d', path));
    this.querySelector('[data-pjt-connector-line]')?.setAttribute('pathLength', '1');
  }

  buildDesktopPath(points) {
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1];
      const current = points[index];
      const isNewRow = Math.abs(current.y - previous.y) > 80 || current.x < previous.x;

      if (isNewRow) {
        const middleY = previous.y + (current.y - previous.y) / 2;
        path += ` L ${previous.x} ${middleY} L ${current.x} ${middleY} L ${current.x} ${current.y}`;
      } else {
        const control = Math.max(36, Math.abs(current.x - previous.x) * .43);
        path += ` C ${previous.x + control} ${previous.y}, ${current.x - control} ${current.y}, ${current.x} ${current.y}`;
      }
    }
    return path;
  }

  buildSquarePath(points) {
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1];
      const current = points[index];
      const sameRow = Math.abs(current.y - previous.y) < 24;
      if (sameRow) {
        path += ` L ${current.x} ${current.y}`;
        continue;
      }

      const middleY = previous.y + (current.y - previous.y) / 2;
      path += ` L ${previous.x} ${middleY} L ${current.x} ${middleY} L ${current.x} ${current.y}`;
    }
    return path;
  }

  onPointerDown = (event) => {
    const card = event.target.closest('[data-pjt-card]');
    if (!card || !this.contains(card)) return;

    card.classList.remove('is-clicked');
    requestAnimationFrame(() => card.classList.add('is-clicked'));
    clearTimeout(this.clickTimer);
    this.clickTimer = setTimeout(() => card.classList.remove('is-clicked'), 520);

    const ripple = card.querySelector('[data-pjt-ripple]');
    if (!ripple || this.reducedMotion.matches) return;
    const inner = card.querySelector('[data-pjt-interactive]');
    const rect = inner?.getBoundingClientRect();
    if (!rect) return;
    ripple.style.left = `${event.clientX - rect.left}px`;
    ripple.style.top = `${event.clientY - rect.top}px`;
    ripple.classList.remove('is-active');
    requestAnimationFrame(() => ripple.classList.add('is-active'));
    ripple.addEventListener('animationend', () => ripple.classList.remove('is-active'), { once: true });
  };

  onPointerMove = (event) => {
    if (this.dataset.enableCardTilt !== 'true' || this.reducedMotion.matches || !this.desktopPointer.matches) return;
    const card = event.target.closest('[data-pjt-card]');
    if (!card || !this.contains(card)) return;
    const inner = card.querySelector('[data-pjt-interactive]');
    if (!inner) return;

    const rect = inner.getBoundingClientRect();
    const horizontal = ((event.clientX - rect.left) / rect.width - .5) * 2;
    const vertical = ((event.clientY - rect.top) / rect.height - .5) * 2;
    cancelAnimationFrame(this.tiltFrame);
    this.tiltFrame = requestAnimationFrame(() => {
      this.cards.forEach((item) => {
        if (item !== card) this.resetCardTilt(item);
      });
      inner.style.setProperty('--pjt-tilt-x', `${horizontal * 2.4}deg`);
      inner.style.setProperty('--pjt-tilt-y', `${vertical * -2.4}deg`);
    });
  };

  resetCardTilt(card) {
    const inner = card.querySelector('[data-pjt-interactive]');
    inner?.style.setProperty('--pjt-tilt-x', '0deg');
    inner?.style.setProperty('--pjt-tilt-y', '0deg');
  }

  resetCardTilts = () => {
    this.cards.forEach((card) => this.resetCardTilt(card));
  };

  onBlockSelect = (event) => {
    const card = event.target.closest?.('[data-pjt-card]');
    if (!card || !this.contains(card)) return;
    this.cards.forEach((item) => item.classList.toggle('is-editor-selected', item === card));
    card.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    this.scheduleConnector();
  };

  setupReveal() {
    if (this.dataset.revealAnimation !== 'true' || !('IntersectionObserver' in window)) {
      this.classList.add('is-revealed', 'is-in-view');
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('is-in-view', entry.isIntersecting);
        if (entry.isIntersecting) {
          this.classList.add('is-revealed');
          this.scheduleConnector();
        }
      },
      { rootMargin: '120px 0px', threshold: .06 }
    );
    this.observer.observe(this);
  }
}

if (!customElements.get('premium-journey-timeline')) {
  customElements.define('premium-journey-timeline', PremiumJourneyTimeline);
}
